const CLAUDE_API_URL = "https://api.anthropic.com/v1/messages"

export interface ClaudeActionResponse {
    action: {
        type: "POINTER" | "TEXT" | "CONTROL"
        inputType: "CLICK" | "DOUBLE_CLICK" | "RIGHT_CLICK" | "ENTER" | "KEY" | "WAIT" | "SCROLL" | "HOVER" | "PASSWORD"
        text?: string
        x?: number  // Pixel coordinates (will be scaled)
        y?: number
    } | null
    explanation: string
    isComplete: boolean
}

// Calculate scale factor to meet Claude API image constraints
// Max 1568px on longest edge, ~1.15 megapixels total
function getScaleFactor(width: number, height: number): number {
    const longEdge = Math.max(width, height)
    const totalPixels = width * height

    const longEdgeScale = 1568 / longEdge
    const totalPixelsScale = Math.sqrt(1_150_000 / totalPixels)

    return Math.min(1.0, longEdgeScale, totalPixelsScale)
}

// Resize image using canvas
async function resizeImage(base64: string, targetWidth: number, targetHeight: number): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
            const canvas = document.createElement("canvas")
            canvas.width = targetWidth
            canvas.height = targetHeight
            const ctx = canvas.getContext("2d")
            if (!ctx) {
                reject(new Error("Failed to get canvas context"))
                return
            }
            ctx.drawImage(img, 0, 0, targetWidth, targetHeight)
            // Get base64 without the data URL prefix
            const resizedBase64 = canvas.toDataURL("image/png").split(",")[1]
            resolve(resizedBase64)
        }
        img.onerror = reject
        img.src = "data:image/png;base64," + base64
    })
}

const DEFAULT_SYSTEM_PROMPT = `You are a test automation assistant helping to record UI test actions.

YOUR TASK: {USE_CASE}

ACTIONS ALREADY COMPLETED:
{ACTIONS}

CRITICAL INSTRUCTIONS:
1. The image provided IS the current screenshot - DO NOT request another screenshot
2. Analyze what you see and IMMEDIATELY perform the next action (click, type, etc.)
3. Use the computer tool with left_click action to click on UI elements
4. Be precise with click coordinates - aim for the CENTER of the target element
5. For taskbar icons at the bottom: y coordinate should be close to the bottom edge (around 95% of screen height)

When the task is complete, respond with text saying "TASK_COMPLETE" instead of using a tool.

IMPORTANT: Do NOT use the screenshot action - you already have the current screen. Just click or type directly.`

const DEFAULT_USER_PROMPT = "This is the current screen. Perform the next action NOW using left_click, type, or key. Do not request a screenshot - act immediately on what you see."

export class ClaudeService {
    private apiKey: string
    private model: string
    private systemPromptTemplate: string
    private userPrompt: string

    constructor(apiKey: string, model?: string, systemPrompt?: string, userPrompt?: string) {
        this.apiKey = apiKey
        this.model = model || "claude-opus-4-6"
        this.systemPromptTemplate = systemPrompt || DEFAULT_SYSTEM_PROMPT
        this.userPrompt = userPrompt || DEFAULT_USER_PROMPT
    }

    async analyzeScreenAndGetAction(
        screenshotBase64: string,
        useCase: string,
        executedActions: string[],
        screenWidth: number = 1920,
        screenHeight: number = 1080,
        abortSignal?: AbortSignal
    ): Promise<ClaudeActionResponse> {
        // Calculate scale factor and scaled dimensions
        const scale = getScaleFactor(screenWidth, screenHeight)
        const scaledWidth = Math.floor(screenWidth * scale)
        const scaledHeight = Math.floor(screenHeight * scale)

        console.log(`[ClaudeService] Original: ${screenWidth}x${screenHeight}, Scale: ${scale.toFixed(3)}, Scaled: ${scaledWidth}x${scaledHeight}`)

        // Resize the screenshot to match what we tell Claude
        const resizedScreenshot = await resizeImage(screenshotBase64, scaledWidth, scaledHeight)

        // Build the system prompt
        const systemPrompt = this.buildSystemPrompt(useCase, executedActions)

        const response = await fetch(CLAUDE_API_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-api-key": this.apiKey,
                "anthropic-version": "2023-06-01",
                "anthropic-beta": "computer-use-2025-11-24",
                "anthropic-dangerous-direct-browser-access": "true"
            },
            signal: abortSignal,
            body: JSON.stringify({
                model: this.model,
                max_tokens: 1024,
                system: systemPrompt,
                tools: [
                    {
                        type: "computer_20251124",
                        name: "computer",
                        display_width_px: scaledWidth,
                        display_height_px: scaledHeight
                    }
                ],
                messages: [{
                    role: "user",
                    content: [
                        {
                            type: "image",
                            source: {
                                type: "base64",
                                media_type: "image/png",
                                data: resizedScreenshot
                            }
                        },
                        {
                            type: "text",
                            text: this.userPrompt
                        }
                    ]
                }]
            })
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Claude API error: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        return this.parseResponse(data, scale)
    }

    private buildSystemPrompt(useCase: string, executedActions: string[]): string {
        const actionsText = executedActions.length
            ? executedActions.map((a, i) => `${i + 1}. ${a}`).join("\n")
            : "None yet - this is the first action"
        return this.systemPromptTemplate
            .replace(/\{USE_CASE\}/g, useCase)
            .replace(/\{ACTIONS\}/g, actionsText)
    }

    private parseResponse(data: any, scale: number): ClaudeActionResponse {
        console.log("[ClaudeService] Raw response:", JSON.stringify(data.content))

        // Check for task completion (text response without tool use)
        const textContent = data.content.find((c: any) => c.type === "text")
        if (textContent && textContent.text.includes("TASK_COMPLETE")) {
            return {
                action: null,
                explanation: textContent.text,
                isComplete: true
            }
        }

        // Look for tool_use in the response
        const toolUse = data.content.find((c: any) => c.type === "tool_use")
        if (!toolUse) {
            // No tool use - might be asking a question or complete
            return {
                action: null,
                explanation: textContent?.text || "No action needed",
                isComplete: true
            }
        }

        const input = toolUse.input
        const action = input.action

        console.log(`[ClaudeService] Tool use: action=${action}, input=`, JSON.stringify(input))

        // Map computer-use actions to our action format
        let actionType: "POINTER" | "TEXT" | "CONTROL" = "POINTER"
        let inputType: string = "CLICK"
        let text: string | undefined
        let x: number | undefined
        let y: number | undefined

        switch (action) {
            case "left_click":
                actionType = "POINTER"
                inputType = "CLICK"
                if (input.coordinate) {
                    // Scale coordinates back to original screen size
                    x = input.coordinate[0] / scale
                    y = input.coordinate[1] / scale
                }
                break

            case "double_click":
                actionType = "POINTER"
                inputType = "DOUBLE_CLICK"
                if (input.coordinate) {
                    x = input.coordinate[0] / scale
                    y = input.coordinate[1] / scale
                }
                break

            case "right_click":
                actionType = "POINTER"
                inputType = "RIGHT_CLICK"
                if (input.coordinate) {
                    x = input.coordinate[0] / scale
                    y = input.coordinate[1] / scale
                }
                break

            case "type":
                actionType = "TEXT"
                inputType = "ENTER"
                text = input.text
                break

            case "key":
                actionType = "CONTROL"
                inputType = "KEY"
                text = input.text
                break

            case "scroll":
                actionType = "CONTROL"
                inputType = "SCROLL"
                const direction = input.scroll_direction || "down"
                const amount = input.scroll_amount || 1
                text = `${amount} ${direction}`
                if (input.coordinate) {
                    x = input.coordinate[0] / scale
                    y = input.coordinate[1] / scale
                }
                break

            case "mouse_move":
                actionType = "POINTER"
                inputType = "HOVER"
                if (input.coordinate) {
                    x = input.coordinate[0] / scale
                    y = input.coordinate[1] / scale
                }
                break

            case "screenshot":
                // Claude requested a screenshot but we already provided one
                // This shouldn't happen with the updated prompts, but handle it gracefully
                console.warn("[ClaudeService] Claude requested screenshot but we already provided one. Skipping.")
                return {
                    action: null,
                    explanation: "Screenshot already provided - please click or type directly",
                    isComplete: false
                }

            default:
                console.warn(`[ClaudeService] Unknown action: ${action}`)
                return {
                    action: null,
                    explanation: `Unknown action: ${action}`,
                    isComplete: false
                }
        }

        // Build explanation from any text content
        let explanation = textContent?.text || `Performing ${action}`
        if (x !== undefined && y !== undefined) {
            // Append coordinates to the explanation
            explanation = `${explanation} (${Math.round(x)}, ${Math.round(y)})`
        }

        console.log(`[ClaudeService] Parsed action: type=${actionType}, inputType=${inputType}, x=${x}, y=${y}, text=${text}`)

        return {
            action: {
                type: actionType,
                inputType: inputType as any,
                text,
                x,
                y
            },
            explanation,
            isComplete: false
        }
    }
}
